import React from 'react';
import { Text, View, StyleSheet } from 'react-native';
import { IColumnConfig } from '@/types/admin/dataTable';
import { IUserManagementTableRow, userTypeOptions } from '@/types/admin/userManagement';
import { ADMIN_COLORS } from '@/constants/admin';
import { DesktopOverflowText } from '@/components/common/admin/desktop/DesktopOverflowText';
import { getUserTypeColor } from '@/constants/admin/userRoles';
import { summarizeList, listToTooltip } from '@/utils/admin/tableFormatters';

// Helpers now imported from shared utils

// Column configuration for user management data table (matching admin-web structure)
export const userManagementTableColumns: IColumnConfig<IUserManagementTableRow>[] = [
  {
    key: 'name',
    label: 'NAME',
    sortable: true,
    filterable: false, // Handled by search
    visible: true,
    mobileVisible: true,
    sortType: 'string',
    renderCell: (value: string) => (
      <Text style={styles.nameText} numberOfLines={1}>
        {value}
      </Text>
    ),
  },
  {
    key: 'userType',
    label: 'USER TYPE',
    sortable: true,
    filterable: true,
    visible: true,
    mobileVisible: true,
    sortType: 'string',
    // Use single-select to match admin-web behavior (singleSelect filter operator)
    filterType: 'select',
    filterOptions: userTypeOptions.map(type => ({ 
      label: type, 
      value: type 
    })),
    renderCell: (value: string) => (
      <View style={styles.userTypeBadge}>
        <Text style={[styles.userTypeText, { color: getUserTypeColor(value) }]}>
          {value}
        </Text>
      </View>
    ),
    renderMobileCell: (value: string) => (
      <Text style={[styles.userTypeText, { color: getUserTypeColor(value), textAlign: 'right' }]} numberOfLines={1}>
        {value}
      </Text>
    ),
  },
  {
    key: 'email',
    label: 'EMAIL',
    sortable: true,
    filterable: true,
    visible: true,
    mobileVisible: true,
    sortType: 'string',
    renderCell: (value: string) => (
      <Text style={styles.emailText} numberOfLines={1}>
        {value}
      </Text>
    ),
  },
  {
    key: 'organization',
    label: 'ORGANIZATION',
    sortable: true,
    filterable: true,
    visible: true,
    mobileVisible: true, // Only show on tablet/desktop
    sortType: 'string',
    filterType: 'text',
    renderCell: (value: (string | null)[] | undefined) => {
      const safe = (value || []).filter((v): v is string => !!v);
      if (safe.length === 0) return null;
      const displayText = summarizeList(safe);
      const tooltip = listToTooltip(safe, 'Org');
      return (
        <DesktopOverflowText text={displayText} tooltipText={tooltip} numberOfLines={2} />
      );
    },
    renderMobileCell: (value: (string | null)[] | undefined) => {
      const safe = (value || []).filter((v): v is string => !!v);
      if (safe.length === 0) return <Text style={[styles.emailText, { textAlign: 'right' }]} numberOfLines={1}>—</Text>;
      const displayText = summarizeList(safe);
      return (
        <Text style={[styles.emailText, { textAlign: 'right' }]} numberOfLines={1}>
          {displayText}
        </Text>
      );
    },
  },
  {
    key: 'properties',
    label: 'PROPERTIES',
    sortable: true,
    filterable: true,
    visible: true,
    mobileVisible: true, // Visible on mobile as summarized text
    sortType: 'string',
    filterType: 'text',
    renderCell: (value: (string | null)[] | undefined) => {
      const safe = (value || []).filter((v): v is string => !!v);
      if (safe.length === 0) return null;
      const displayText = summarizeList(safe);
      const tooltip = listToTooltip(safe);
      return (
        <DesktopOverflowText text={displayText} tooltipText={tooltip} numberOfLines={2} />
      );
    },
    renderMobileCell: (value: (string | null)[] | undefined) => {
      const safe = (value || []).filter((v): v is string => !!v);
      if (safe.length === 0) return <Text style={[styles.emailText, { textAlign: 'right' }]} numberOfLines={1}>—</Text>;
      const displayText = summarizeList(safe);
      return (
        <Text style={[styles.emailText, { textAlign: 'right' }]} numberOfLines={1}>
          {displayText}
        </Text>
      );
    },
  },
];

const styles = StyleSheet.create({
  nameText: {
    fontSize: 16,
    fontWeight: '600',
    color: ADMIN_COLORS.DATA_TABLE.TEXT,
  },
  emailText: {
    fontSize: 14,
    color: ADMIN_COLORS.DATA_TABLE.TEXT,
  },
  userTypeBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    backgroundColor: 'rgba(0, 0, 0, 0.05)',
  },
  userTypeText: {
    fontSize: 14,
    fontWeight: '600',
  },
});
